/**
 * Pool API client
 * Handles all pool-related API operations
 */
import type {
  ApiResponse,
  Pool,
  PoolWithMembership,
  Membership,
  MemberWithSubmissionStatus,
  CreatePoolInput,
  UpdatePoolInput,
} from './types';
import { apiClient } from './client';

/**
 * Create a new pool
 * @param input Pool creation data
 * @returns Created pool
 */
export async function createPool(
  input: CreatePoolInput
): Promise<ApiResponse<Pool>> {
  return apiClient.post<Pool>('/pools', input);
}

/**
 * Get pool details by ID
 * @param poolId Pool identifier
 * @returns Pool details with user's membership info
 */
export async function getPool(poolId: string): Promise<ApiResponse<PoolWithMembership>> {
  return apiClient.get<PoolWithMembership>(`/pools/${poolId}`);
}

/**
 * Update pool settings
 * @param poolId Pool identifier
 * @param input Pool update data
 * @returns Updated pool
 */
export async function updatePool(
  poolId: string,
  input: UpdatePoolInput
): Promise<ApiResponse<Pool>> {
  return apiClient.put<Pool>(`/pools/${poolId}`, input);
}

/**
 * Get all members of a pool
 * @param poolId Pool identifier
 * @returns List of pool members with submission status
 */
export async function getPoolMembers(
  poolId: string
): Promise<ApiResponse<MemberWithSubmissionStatus[]>> {
  return apiClient.get<MemberWithSubmissionStatus[]>(`/pools/${poolId}/members`);
}

/**
 * Remove a member from a pool
 * @param poolId Pool identifier
 * @param userId User identifier to remove
 * @returns void on success
 */
export async function removeMember(
  poolId: string,
  userId: string
): Promise<ApiResponse<void>> {
  return apiClient.delete<void>(`/pools/${poolId}/members/${userId}`);
}

/**
 * Promote a member to cohost
 * @param poolId Pool identifier
 * @param userId User identifier to promote
 * @returns Updated membership
 */
export async function promoteToCohost(
  poolId: string,
  userId: string
): Promise<ApiResponse<Membership>> {
  return apiClient.post<Membership>(`/pools/${poolId}/members/${userId}/promote`, {});
}

/**
 * Lock a pool manually
 * @param poolId Pool identifier
 * @returns Updated pool
 */
export async function lockPool(poolId: string): Promise<ApiResponse<Pool>> {
  return apiClient.post<Pool>(`/pools/${poolId}/lock`, {});
}

/**
 * Unlock a pool manually
 * @param poolId Pool identifier
 * @returns Updated pool
 */
export async function unlockPool(poolId: string): Promise<ApiResponse<Pool>> {
  return apiClient.post<Pool>(`/pools/${poolId}/unlock`, {});
}
